/**
 * The `Idempotency-Key` of one attempt to pay (a renewal, a recovery, a plan upgrade), so sending it twice (a double
 * click, a retry, a reload in the middle) pays once and answers with what the first one made. Nothing is shown.
 *
 * One key per *thing being paid for* (`scope`: the domain and the years, the plan and the period): paying for something
 * else is another attempt, so it gets another key. It is kept in `sessionStorage` so a page reloaded mid-payment sends
 * the same key and gets the first payment's result instead of paying again, and dropped only when the attempt has
 * definitely ended (see `attemptEnded`): paid, or refused before any money moved.
 */
import { attemptEnded, newCheckoutKey } from "@/lib/domains";

const PREFIX = "idempotency:";
/** Where the keys live when sessionStorage can't be used (private windows): lost on a reload, never wrong. */
const memory = new Map<string, string>();

function read(name: string): string | null {
  try {
    return sessionStorage.getItem(PREFIX + name) ?? memory.get(name) ?? null;
  } catch {
    return memory.get(name) ?? null;
  }
}

function write(name: string, key: string): void {
  memory.set(name, key);
  try {
    sessionStorage.setItem(PREFIX + name, key);
  } catch {
    /* storage unavailable: the key just won't survive a reload */
  }
}

function drop(name: string): void {
  memory.delete(name);
  try {
    sessionStorage.removeItem(PREFIX + name);
  } catch {
    /* ignore */
  }
}

/** The key for this attempt: the one already made for this `scope`, or a new one. */
export function attemptKey(slot: string, scope: string): string {
  const name = `${slot}:${scope}`;
  const kept = read(name);
  if (kept) return kept;
  const fresh = newCheckoutKey();
  write(name, fresh);
  return fresh;
}

/**
 * The attempt is over. Without `error` it was paid; with one, the key is dropped only if the server definitely refused
 * (nothing was charged). A server error, no answer, or "still processing" keeps it: the payment may have gone through,
 * and asking again with the same key returns its result instead of paying twice.
 */
export function attemptFinished(slot: string, scope: string, error?: unknown): void {
  if (error === undefined || attemptEnded(error)) drop(`${slot}:${scope}`);
}
